import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UserDetailsDrawer = ({ user, isOpen, onClose, onSave }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(user || {});

  const departmentOptions = [
    { value: 'IT', label: 'IT' },
    { value: 'Finance', label: 'Finance' },
    { value: 'HR', label: 'HR' },
    { value: 'Operations', label: 'Operations' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Sales', label: 'Sales' }
  ];

  const roleOptions = [
    { value: 'IT Admin', label: 'IT Admin' },
    { value: 'Asset Manager', label: 'Asset Manager' },
    { value: 'Auditor', label: 'Auditor' },
    { value: 'Standard User', label: 'Standard User' }
  ];

  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Suspended', label: 'Suspended' },
    { value: 'Pending', label: 'Pending' }
  ];

  const activityHistory = [
    {
      id: 1,
      action: 'Login',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      details: 'Successful login from 192.168.1.100',
      type: 'success'
    },
    {
      id: 2,
      action: 'Asset Updated',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      details: 'Updated asset AT-2025-0156 location',
      type: 'info'
    },
    {
      id: 3,
      action: 'Role Changed',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      details: 'Role changed from Standard User to Asset Manager',
      type: 'warning'
    },
    {
      id: 4,
      action: 'Password Reset',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      details: 'Password reset requested and completed',
      type: 'info'
    },
    {
      id: 5,
      action: 'Account Created',
      timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      details: 'User account created by Arjun Sharma',
      type: 'success'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onSave(formData);
    setEditMode(false);
  };

  const handleCancel = () => {
    setFormData(user);
    setEditMode(false);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString();
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'success':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'AlertCircle';
      case 'info':
      default:
        return 'Info';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'success':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-error';
      case 'info':
      default:
        return 'text-accent';
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-card border-l border-border shadow-elevated overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                <span className="text-primary-foreground font-semibold text-lg">
                  {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-card-foreground">{user?.name}</h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {editMode ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSave}
                    iconName="Save"
                    iconPosition="left"
                  >
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditMode(true)}
                  iconName="Edit"
                  iconPosition="left"
                >
                  Edit User
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-card-foreground mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                value={formData?.name || ''}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                disabled={!editMode}
              />
              <Input
                label="Email Address"
                type="email"
                value={formData?.email || ''}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                disabled={!editMode}
              />
              <Select
                label="Department"
                options={departmentOptions}
                value={formData?.department || ''}
                onChange={(value) => handleInputChange('department', value)}
                disabled={!editMode}
              />
              <Select
                label="Role"
                options={roleOptions}
                value={formData?.role || ''}
                onChange={(value) => handleInputChange('role', value)}
                disabled={!editMode}
              />
              <Input
                label="Employee ID"
                value={formData?.employeeId || ''}
                onChange={(e) => handleInputChange('employeeId', e?.target?.value)}
                disabled={!editMode}
              />
              <Select
                label="Account Status"
                options={statusOptions}
                value={formData?.status || ''}
                onChange={(value) => handleInputChange('status', value)}
                disabled={!editMode}
              />
            </div>
          </div>

          {/* Account Details */}
          <div>
            <h3 className="text-lg font-medium text-card-foreground mb-4">Account Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">Created Date</label>
                <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground">
                  {new Date(user.createdAt)?.toLocaleDateString()}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">Last Login</label>
                <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground">
                  {user?.lastLogin ? new Date(user.lastLogin)?.toLocaleString() : 'Never'}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">Login Count</label>
                <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground">
                  {user?.loginCount || 0} times
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">Failed Attempts</label>
                <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground">
                  {user?.failedAttempts || 0} attempts
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h3 className="text-lg font-medium text-card-foreground mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Button
                variant="outline"
                iconName="Key"
                iconPosition="left"
                className="justify-start"
              >
                Reset Password
              </Button>
              <Button
                variant="outline"
                iconName="Unlock"
                iconPosition="left"
                className="justify-start"
              >
                Unlock Account
              </Button>
              <Button
                variant="outline"
                iconName="Mail"
                iconPosition="left"
                className="justify-start"
              >
                Send Welcome Email
              </Button>
              <Button
                variant="outline"
                iconName="LogOut"
                iconPosition="left"
                className="justify-start"
              >
                Force Logout
              </Button>
            </div>
          </div>

          {/* Activity History */}
          <div>
            <h3 className="text-lg font-medium text-card-foreground mb-4">Activity History</h3>
            <div className="space-y-3">
              {activityHistory?.map((activity) => (
                <div key={activity?.id} className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg">
                  <div className={`mt-0.5 ${getActivityColor(activity?.type)}`}>
                    <Icon name={getActivityIcon(activity?.type)} size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-card-foreground">{activity?.action}</h4>
                      <span className="text-xs text-muted-foreground">
                        {formatTimestamp(activity?.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{activity?.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Permissions Summary */}
          <div>
            <h3 className="text-lg font-medium text-card-foreground mb-4">Current Permissions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border border-border rounded-lg">
                <h4 className="font-medium text-card-foreground mb-2">System Access</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">User Management</span>
                    <Icon name="Check" size={16} className="text-success" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Asset Management</span>
                    <Icon name="Check" size={16} className="text-success" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Audit Management</span>
                    <Icon name="X" size={16} className="text-error" />
                  </div>
                </div>
              </div>
              <div className="p-4 border border-border rounded-lg">
                <h4 className="font-medium text-card-foreground mb-2">Data Access</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">View Reports</span>
                    <Icon name="Check" size={16} className="text-success" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Export Data</span>
                    <Icon name="Check" size={16} className="text-success" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">System Config</span>
                    <Icon name="X" size={16} className="text-error" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsDrawer;